// src/pages/Sales/ProductSearchPanel.jsx
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  Alert,
  Skeleton,
} from "@mui/material";
import { Search as SearchIcon, Add as AddIcon } from "@mui/icons-material";

function formatPrice(v) {
  const n = Number(v || 0);
  return `${n.toLocaleString("vi-VN")}₫`;
}

export default function ProductSearchPanel({
  t,
  searchTerm,
  setSearchTerm,
  filteredProducts,
  onSelectProduct,
  loading = false,  
  error = "",
}) {
  const THUMB = 180; 

  return (
    <>
      <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">
        {t("Search Products")}
      </Typography>

      <TextField
        fullWidth
        placeholder={t("Search by name or SKU...")}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="primary" />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      {/* Trạng thái lỗi */}
      {!!error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        {/* Loading skeleton */}
        {loading ? (
          <Grid container spacing={2}>
            {Array.from({ length: 5 }).map((_, i) => (
              <Grid item xs={12} key={i}>
                <Card variant="outlined">
                  <CardContent sx={{ display: "flex", alignItems: "center", p: 1.5, gap: 2 }}>
                    <Skeleton variant="rounded" width={THUMB} height={THUMB} />
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Skeleton width="60%" />
                      <Skeleton width="40%" />
                      <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                        <Skeleton variant="rounded" width={80} height={24} />
                        <Skeleton variant="rounded" width={100} height={24} />
                      </Box>
                    </Box>
                    <Skeleton variant="circular" width={32} height={32} />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : filteredProducts.length === 0 ? (
          // Empty state
          <Box sx={{ textAlign: "center", py: 4 }}>
            <SearchIcon sx={{ fontSize: 48, color: "text.secondary", mb: 1 }} />
            <Typography color="text.secondary">Không tìm thấy sản phẩm nào</Typography>
          </Box>
        ) : (
          // List sản phẩm
          <Grid container spacing={2}>
            {filteredProducts.map((product) => {
              const outOfStock = !product.stock || product.stock <= 0;
              return (
                <Grid item xs={12} key={product.id}>
                  <Card
                    variant="outlined"
                    sx={{
                      cursor: outOfStock ? "default" : "pointer",
                      transition: "all 0.2s",
                      opacity: outOfStock ? 0.7 : 1,
                      "&:hover": !outOfStock
                        ? { transform: "translateY(-2px)", boxShadow: 2, borderColor: "primary.main" }
                        : undefined,
                    }}
                    onClick={() => {
                      if (!outOfStock && onSelectProduct) onSelectProduct(product);
                    }}
                  >
                    <CardContent sx={{ display: "flex", alignItems: "center", p: 1.5, gap: 2 }}>
                      <Avatar
                        src={product.image}
                        variant="rounded"
                        sx={{ width: THUMB, height: THUMB }}
                      >
                        {product.name?.charAt(0)}
                      </Avatar>

                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight="bold" noWrap>
                          {product.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          SKU: {product.sku}
                        </Typography>

                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
                          <Chip label={formatPrice(product.price)} size="small" color="primary" variant="outlined" />
                          <Chip
                            label={`Tồn kho: ${product.stock || 0}`}
                            size="small"
                            color={outOfStock ? "default" : "success"}
                            variant={outOfStock ? "outlined" : "filled"}
                          />
                          {outOfStock && <Chip label="Hết hàng" size="small" color="default" />}
                        </Box>
                      </Box>

                      <Tooltip title={outOfStock ? "Hết hàng" : "Thêm vào giỏ"}>
                        <span>
                          <IconButton
                            color="primary"
                            disabled={outOfStock}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!outOfStock && onSelectProduct) onSelectProduct(product);
                            }}
                          >
                            <AddIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>
    </>
  );
}
